import { useTranslations } from '@vocab/react';
import type React from 'react';
import { Route, Switch } from 'react-router-dom';

import {
  PAGE_ID_CONCIERGE,
  PAGE_ID_GUARANTEED_HIRE,
} from 'src/app/constants/pageUid';
import { routeNameToPage } from 'src/app/routes/routeNameToPage';
import { withAppEnhancers } from 'src/app/views/enhancers/withAppEnhancers';
import { useCopyJob } from 'src/app/views/hooks/useCopyJob';
import { useCreateJob } from 'src/app/views/hooks/useCreateJob';
import { useEditDraft } from 'src/app/views/hooks/useEditDraft';
import { useExperiments } from 'src/app/views/hooks/useExperiments';
import { useJobId } from 'src/app/views/hooks/useJobId';
import { useLastValidRoute } from 'src/app/views/hooks/useLastValidRoute';
import { useRefreshAdProductSet } from 'src/app/views/hooks/useRefreshProductSetForAdPosting';
import { useSelectedProduct } from 'src/app/views/hooks/useSelectedProduct';
import { useSelectedRRQuestions } from 'src/app/views/hooks/useSelectedRRQuestions';
import { useTealiumCreateFlowType } from 'src/app/views/hooks/useTealiumCreateFlowType';
import {
  ConciergePage,
  GuaranteedHirePage,
} from 'src/app/views/pages/JobAdBooking';
import { SeoTitle } from 'src/app/views/shared/SeoTitle';
import type { ContainerProps } from 'src/types/app';

import { useUpdateDraftIdParam } from '../../hooks/useUpdateDraftIdParam';
import translations from '../.vocab';

const CreateFlowContainer = ({ routes }: ContainerProps): React.ReactNode => {
  useExperiments();
  useJobId();
  useRefreshAdProductSet();
  useCreateJob();
  useSelectedRRQuestions();
  useSelectedProduct();
  useUpdateDraftIdParam();

  return (
    <Switch>
      {routes.map(({ name, route }) => {
        let CurrentRouteComponent = routeNameToPage[name];

        if (name === PAGE_ID_CONCIERGE) {
          CurrentRouteComponent = ConciergePage;
        } else if (name === PAGE_ID_GUARANTEED_HIRE) {
          CurrentRouteComponent = GuaranteedHirePage;
        }

        return (
          <Route
            key={name}
            path={route}
            component={CurrentRouteComponent}
            exact={true}
          />
        );
      })}
    </Switch>
  );
};

export const CreateFlowWithInitialData = ({ routes }: ContainerProps) => {
  const { t } = useTranslations(translations);

  const { loading: isEditDraftLoading } = useEditDraft();
  const { loading: isCopyJobLoading } = useCopyJob();
  const { loading: isLastValidRouteLoading } = useLastValidRoute();

  useTealiumCreateFlowType();

  return (
    <>
      <SeoTitle title={t('create-container-seo-title')} />
      {isEditDraftLoading ||
      isCopyJobLoading ||
      isLastValidRouteLoading ? null : (
        <CreateFlowContainer routes={routes} />
      )}
    </>
  );
};

export default withAppEnhancers(CreateFlowWithInitialData);
